import { useCallback, useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { debugLog } from "@/lib/debug";
import WorkOrderForm from "@/components/WorkOrderForm";
import WorkOrderList from "@/components/WorkOrderList";
import PropertyStats from "@/components/PropertyStats";
import DailyReportPanel from "@/components/DailyReportPanel";
import LoginPage from "@/components/LoginPage";
import CompanyStats from "@/components/CompanyStats";
import SettingsPanel from "@/components/SettingsPanel";
import MyRequests from "@/components/MyRequests";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, BarChart3, Mail, PlusCircle, LogOut, Building2, Settings2, History } from "lucide-react";

// Same-origin API base: whatever domain serves the app (preview or custom) also proxies /api/*.
// Using the build-time REACT_APP_BACKEND_URL breaks sessions on a custom domain because the
// Set-Cookie from the API host becomes a blocked third-party cookie.
const API = `${window.location.origin}/api`;
axios.defaults.withCredentials = true;
const IDLE_EVENTS = ["mousedown", "keydown", "scroll", "touchstart", "mousemove"];
const IDLE_TIMEOUT_MS = 60 * 60 * 1000;

function useSessionBootstrap(setUser) {
  useEffect(() => {
    axios.interceptors.response.use(
      (r) => r,
      async (err) => {
        const original = err.config || {};
        if (err.response?.status === 401 && !original._retried && !(original.url || "").includes("/auth/")) {
          original._retried = true;
          try {
            await axios.post(`${API}/auth/refresh`);
            return axios(original);
          } catch (refreshErr) {
            debugLog("session refresh failed:", refreshErr);
            setUser(null);
          }
        }
        return Promise.reject(err);
      }
    );
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/me`);
        setUser(r.data);
      } catch {
        setUser(null);
      }
    })();
    // mount-only: registers the 401 interceptor once and restores any existing session
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function useMeta(user, metaKey, setMeta) {
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const m = await axios.get(`${API}/meta`);
        setMeta(m.data);
        if (user.role === "admin") await axios.post(`${API}/seed`).catch((e) => debugLog("seed skipped:", e));
      } catch (e) {
        debugLog("meta load failed:", e);
        toast.error("Failed to load form options");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- API/axios are module-scope constants
  }, [user, metaKey]);
}

function useSignOut(setUser) {
  return useCallback(
    async (idle) => {
      try {
        await axios.post(`${API}/auth/logout`);
      } catch (e) {
        debugLog("logout request failed; signing out locally anyway:", e);
      }
      setUser(null);
      if (idle) toast.info("Signed out due to 1 hour of inactivity");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- API/axios are module-scope constants
    [setUser]
  );
}

function useIdleLogout(user, signOut) {
  useEffect(() => {
    if (!user) return;
    let timer;
    const reset = () => { clearTimeout(timer); timer = setTimeout(() => signOut(true), IDLE_TIMEOUT_MS); };
    let last = 0;
    const onActivity = () => { const now = Date.now(); if (now - last > 5000) { last = now; reset(); } };
    IDLE_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    reset();
    return () => { clearTimeout(timer); IDLE_EVENTS.forEach((e) => window.removeEventListener(e, onActivity)); };
  }, [user, signOut]);
}

function AppHeader({ user, onLogout }) {
  return (
    <header className="sticky top-0 z-40 bg-[#478261] border-b border-[#3a6b51]">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/workplaceone-logo.jpg" alt="WorkplaceOne" className="h-12 w-auto object-contain rounded" data-testid="site-logo" />
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-black/70">WorkplaceOne</div>
            <h1 className="text-lg font-semibold tracking-tight text-black">WP1 DeskPoint</h1>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-black/80 font-mono">
          <span className="hidden lg:inline-flex items-center gap-2"><span className="inline-block w-2 h-2 rounded-full bg-emerald-300" /> Live &middot; Toronto</span>
          <span className="hidden lg:inline text-black/30">|</span>
          <span className="hidden sm:inline" data-testid="current-user-email">{user.email} ({user.role})</span>
          <Button variant="outline" size="sm" onClick={onLogout} data-testid="logout-button" className="bg-white text-black hover:bg-slate-100 border-white transition-colors shrink-0">
            <LogOut className="w-4 h-4 mr-1.5" />Sign Out
          </Button>
        </div>
      </div>
    </header>
  );
}

function MainTabs({ isAdmin }) {
  const cls = "px-4 py-2 data-[state=active]:bg-[#478261] data-[state=active]:text-white";
  return (
    <TabsList className="bg-white border border-slate-200 p-1 h-auto rounded-lg mb-6 flex flex-wrap gap-1" data-testid="main-tabs">
      {isAdmin && (
        <TabsTrigger value="dashboard" data-testid="tab-dashboard" className={cls}>
          <LayoutDashboard className="w-4 h-4 mr-2" />Dashboard
        </TabsTrigger>
      )}
      <TabsTrigger value="submit" data-testid="tab-submit" className={cls}>
        <PlusCircle className="w-4 h-4 mr-2" />New Request
      </TabsTrigger>
      <TabsTrigger value="my" data-testid="tab-my-requests" className={cls}>
        <History className="w-4 h-4 mr-2" />My Requests
      </TabsTrigger>
      {isAdmin && (
        <>
          <TabsTrigger value="stats" data-testid="tab-stats" className={cls}>
            <BarChart3 className="w-4 h-4 mr-2" />Property Stats
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports" className={cls}>
            <Mail className="w-4 h-4 mr-2" />Reports
          </TabsTrigger>
          <TabsTrigger value="companies" data-testid="tab-companies" className={cls}>
            <Building2 className="w-4 h-4 mr-2" />Companies
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings" className={cls}>
            <Settings2 className="w-4 h-4 mr-2" />Settings
          </TabsTrigger>
        </>
      )}
    </TabsList>
  );
}

function AdminTabContents({ meta, refreshKey, onChanged, onSaved, user }) {
  return (
    <>
      <TabsContent value="dashboard">
        <WorkOrderList api={API} meta={meta} refreshKey={refreshKey} onChanged={onChanged} />
      </TabsContent>
      <TabsContent value="stats">
        <PropertyStats api={API} meta={meta} refreshKey={refreshKey} />
      </TabsContent>
      <TabsContent value="reports">
        <DailyReportPanel api={API} />
      </TabsContent>
      <TabsContent value="companies">
        <CompanyStats api={API} />
      </TabsContent>
      <TabsContent value="settings">
        <SettingsPanel api={API} onSaved={onSaved} currentUser={user} />
      </TabsContent>
    </>
  );
}

function App() {
  const [user, setUser] = useState(undefined); // undefined=checking, null=logged out
  const [tab, setTab] = useState("submit");
  const [refreshKey, setRefreshKey] = useState(0);
  const [metaKey, setMetaKey] = useState(0);
  const [meta, setMeta] = useState({ properties: [], issue_types: [] });

  const isAdmin = user?.role === "admin";
  const signOut = useSignOut(setUser);
  useSessionBootstrap(setUser);
  useMeta(user, metaKey, setMeta);
  useIdleLogout(user, signOut);

  useEffect(() => {
    if (user?.role === "admin") setTab("dashboard");
  }, [user]);

  const bump = () => setRefreshKey((k) => k + 1);
  const reloadMeta = () => setMetaKey((k) => k + 1);

  if (user === undefined) {
    return <div className="min-h-screen bg-slate-50 grid place-items-center text-slate-500 text-sm font-mono" data-testid="auth-loading">Loading…</div>;
  }
  const resetToken = new URLSearchParams(window.location.search).get("reset_token");
  if (user === null || resetToken) {
    return (<><Toaster position="top-right" richColors /><LoginPage api={API} onLogin={(u) => setUser(u)} /></>);
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="app-root">
      <Toaster position="top-right" richColors />
      <AppHeader user={user} onLogout={() => signOut(false)} />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={tab} onValueChange={setTab}>
          <MainTabs isAdmin={isAdmin} />
          <TabsContent value="submit">
            <WorkOrderForm
              api={API}
              meta={meta}
              defaultName={user.name}
              isAdmin={isAdmin}
              onCreated={() => {
                toast.success("Work order submitted");
                bump();
                if (isAdmin) setTab("dashboard");
              }}
            />
          </TabsContent>
          <TabsContent value="my">
            <MyRequests api={API} onDuplicated={bump} />
          </TabsContent>
          {isAdmin && <AdminTabContents meta={meta} refreshKey={refreshKey} onChanged={bump} onSaved={reloadMeta} user={user} />}
        </Tabs>
      </main>

      <footer className="max-w-7xl mx-auto px-6 pb-8 text-xs text-slate-500 font-mono">
        Daily digest auto-sent to d.viola@workplaceone.com on weekdays (Eastern Time — New York). Sessions sign out after 1 hour of inactivity.
      </footer>
    </div>
  );
}

export default App;
